import { computed, ref, watch, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { PRICING_CONTROL_CONFIG, PRICING_VALIDATION, PricingAction } from '../constants'
import type { PartPricingItem, PricingControlState, PricingValidationError } from '../types'

export interface ValidationRule {
  id: string
  name: string
  validate: (item: PartPricingItem) => string | null
  severity: 'error' | 'warning' | 'info'
  enabled: boolean
}

export interface ValidationSummary {
  isValid: boolean
  errorCount: number
  warningCount: number
  infoCount: number
  errors: PricingValidationError[]
  warnings: PricingValidationError[]
  infos: PricingValidationError[]
}

export function useBillPartPricingValidation(state: Ref<PricingControlState>) {
  const { t } = useI18n()

  // Validation configuration
  const validationConfig = ref({
    enableRealTimeValidation: true,
    enableBusinessRules: true,
    enablePriceRangeValidation: true,
    enablePercentageValidation: true,
    enableCurrencyValidation: true,
    strictMode: false,
  })

  // Custom validation rules
  const customRules = ref<ValidationRule[]>([])

  // Built-in validation rules
  const builtInRules: ValidationRule[] = [
    {
      id: 'price-minimum',
      name: 'Minimum Price Validation',
      validate: (item: PartPricingItem) => {
        if (item.action === PricingAction.UPDATE && item.proposedPrice < PRICING_VALIDATION.MIN_PRICE) {
          return t('composites.BillPartPricingControl.validation.priceMinimum', { name: item.name })
        }
        return null
      },
      severity: 'error',
      enabled: true,
    },
    {
      id: 'price-maximum',
      name: 'Maximum Price Validation',
      validate: (item: PartPricingItem) => {
        if (item.action === PricingAction.UPDATE && item.proposedPrice > PRICING_VALIDATION.MAX_PRICE) {
          return t('composites.BillPartPricingControl.validation.priceMaximum', { name: item.name })
        }
        return null
      },
      severity: 'error',
      enabled: true,
    },
    {
      id: 'zero-price',
      name: 'Zero Price Validation',
      validate: (item: PartPricingItem) => {
        if (item.action === PricingAction.UPDATE && item.proposedPrice === 0) {
          return t('composites.BillPartPricingControl.validation.zeroPrice', { name: item.name })
        }
        return null
      },
      severity: 'error',
      enabled: true,
    },
    {
      id: 'negative-price',
      name: 'Negative Price Validation',
      validate: (item: PartPricingItem) => {
        if (item.action === PricingAction.UPDATE && item.proposedPrice < 0) {
          return t('composites.BillPartPricingControl.validation.negativePrice', { name: item.name })
        }
        return null
      },
      severity: 'error',
      enabled: true,
    },
    {
      id: 'currency-consistency',
      name: 'Currency Consistency Validation',
      validate: (item: PartPricingItem) => {
        if (item.action === PricingAction.UPDATE && !item.currency) {
          return t('composites.BillPartPricingControl.validation.missingCurrency', { name: item.name })
        }
        return null
      },
      severity: 'warning',
      enabled: true,
    },
    {
      id: 'precision-validation',
      name: 'Price Precision Validation',
      validate: (item: PartPricingItem) => {
        if (item.action === PricingAction.UPDATE) {
          const decimalPlaces = (item.proposedPrice.toString().split('.')[1] || '').length
          if (decimalPlaces > 2) {
            return t('composites.BillPartPricingControl.validation.pricePrecision', { name: item.name })
          }
        }
        return null
      },
      severity: 'warning',
      enabled: true,
    },
  ]

  // All validation rules (built-in + custom)
  const allRules = computed(() => [...builtInRules.filter((rule) => rule.enabled), ...customRules.value.filter((rule) => rule.enabled)])

  // Validate a single item
  const validateItem = (item: PartPricingItem): PricingValidationError[] => {
    const errors: PricingValidationError[] = []

    allRules.value.forEach((rule) => {
      const errorMessage = rule.validate(item)
      if (errorMessage) {
        errors.push({
          itemId: item.id,
          field: rule.id,
          message: errorMessage,
          severity: rule.severity,
          ruleName: rule.name,
        })
      }
    })

    return errors
  }

  // Validate all items
  const validateAllItems = (): ValidationSummary => {
    const allErrors: PricingValidationError[] = []
    const allWarnings: PricingValidationError[] = []
    const allInfos: PricingValidationError[] = []

    state.value.items.forEach((item) => {
      const itemErrors = validateItem(item)
      itemErrors.forEach((error) => {
        switch (error.severity) {
          case 'error':
            allErrors.push(error)
            break
          case 'warning':
            allWarnings.push(error)
            break
          case 'info':
            allInfos.push(error)
            break
        }
      })
    })

    return {
      isValid: allErrors.length === 0,
      errorCount: allErrors.length,
      warningCount: allWarnings.length,
      infoCount: allInfos.length,
      errors: allErrors,
      warnings: allWarnings,
      infos: allInfos,
    }
  }

  // Real-time validation results
  const validationSummary = computed(() => {
    if (!validationConfig.value.enableRealTimeValidation) {
      return {
        isValid: true,
        errorCount: 0,
        warningCount: 0,
        infoCount: 0,
        errors: [],
        warnings: [],
        infos: [],
      }
    }
    return validateAllItems()
  })

  // Validation errors for display
  const validationErrors = computed(() => validationSummary.value.errors.map((error) => error.message))

  // Validation warnings for display
  const validationWarnings = computed(() => validationSummary.value.warnings.map((warning) => warning.message))

  // Check if specific item has errors
  const hasItemErrors = (itemId: string): boolean => {
    return validationSummary.value.errors.some((error) => error.itemId === itemId)
  }

  // Get errors for specific item
  const getItemErrors = (itemId: string): PricingValidationError[] => {
    return validationSummary.value.errors.filter((error) => error.itemId === itemId)
  }

  // Get warnings for specific item
  const getItemWarnings = (itemId: string): PricingValidationError[] => {
    return validationSummary.value.warnings.filter((warning) => warning.itemId === itemId)
  }

  // Validate before applying changes
  const validateBeforeApply = (): { canApply: boolean; errors: string[] } => {
    const summary = validateAllItems()

    // Check for pending decisions
    const pendingItems = state.value.items.filter((item) => item.action === PricingAction.PENDING)
    if (pendingItems.length > 0) {
      summary.errors.push({
        itemId: '',
        field: 'pending-decisions',
        message: t('composites.BillPartPricingControl.validation.pendingDecisions', { count: pendingItems.length }),
        severity: 'error',
        ruleName: 'Pending Decisions',
      })
    }

    return {
      canApply: summary.errors.length === 0,
      errors: summary.errors.map((error) => error.message),
    }
  }

  // Add custom validation rule
  const addCustomRule = (rule: ValidationRule): void => {
    customRules.value.push(rule)
  }

  // Remove custom validation rule
  const removeCustomRule = (ruleId: string): void => {
    const index = customRules.value.findIndex((rule) => rule.id === ruleId)
    if (index !== -1) {
      customRules.value.splice(index, 1)
    }
  }

  // Enable/disable validation rule
  const toggleRule = (ruleId: string, enabled: boolean): void => {
    const builtInRule = builtInRules.find((rule) => rule.id === ruleId)
    if (builtInRule) {
      builtInRule.enabled = enabled
    }

    const customRule = customRules.value.find((rule) => rule.id === ruleId)
    if (customRule) {
      customRule.enabled = enabled
    }
  }

  // Reset validation configuration
  const resetValidationConfig = (): void => {
    validationConfig.value = {
      enableRealTimeValidation: true,
      enableBusinessRules: true,
      enablePriceRangeValidation: true,
      enablePercentageValidation: true,
      enableCurrencyValidation: true,
      strictMode: false,
    }

    builtInRules.forEach((rule) => {
      rule.enabled = true
    })

    customRules.value = []
  }

  // Watch for changes and validate in real-time
  watch(
    () => state.value.items,
    () => {
      if (validationConfig.value.enableRealTimeValidation) {
        // Validation is automatically computed via validationSummary
      }
    },
    { deep: true },
  )

  return {
    // Configuration
    validationConfig,

    // Rules management
    allRules,
    builtInRules,
    customRules,
    addCustomRule,
    removeCustomRule,
    toggleRule,
    resetValidationConfig,

    // Validation methods
    validateItem,
    validateAllItems,
    validateBeforeApply,

    // Computed validation results
    validationSummary,
    validationErrors,
    validationWarnings,

    // Item-specific validation
    hasItemErrors,
    getItemErrors,
    getItemWarnings,
  }
}

// Extended PricingValidationError interface
declare module '../types' {
  interface PricingValidationError {
    severity?: 'error' | 'warning' | 'info'
    ruleName?: string
  }
}
